import { Menu as MenuIcon, XIcon } from 'lucide-react'
import type { ComponentPropsWithoutRef, ElementType, HTMLAttributes, ReactNode } from 'react'
import { useEffect } from 'react'
import React, { useCallback, useId, useRef, useState } from 'react'
import clsx from 'clsx'
import type { UseAnchoredPositionOptions } from '@/src/hooks/useAnchoredPosition'
import { useAnchoredPosition } from '@/src/hooks/useAnchoredPosition'
import { ExpansionIcon } from '@/src/components/display-and-visualization/ExpansionIcon'
import { useOverlayRegistry } from '@/src/hooks/useOverlayRegistry'
import { useHightideTranslation } from '@/src/i18n/useHightideTranslation'
import { IconButton } from '../../user-interaction/IconButton'

type SimpleNavigationItem = {
  label: ReactNode,
  link: string,
  external?: boolean,
}

// TODO allow for nesting deeper than one level
type SubItemNavigationItem = {
  label: ReactNode,
  items?: SimpleNavigationItem[],
}

export type NavigationItemType = SimpleNavigationItem | SubItemNavigationItem

export type NavigationLinkComponentProps = ComponentPropsWithoutRef<'a'>

const DefaultLink: ElementType<NavigationLinkComponentProps> = 'a'

function isSubItem(item: NavigationItemType): item is SubItemNavigationItem {
  return 'items' in item && Array.isArray(item.items)
}

///
/// NavigationItemWithSubItem
///
type NavigationItemWithSubItemProps = SubItemNavigationItem & UseAnchoredPositionOptions & {
  LinkComponent?: ElementType<NavigationLinkComponentProps>,
}

const NavigationItemWithSubItem = ({
  items,
  label,
  horizontalAlignment = 'center',
  LinkComponent = DefaultLink,
  ...options
}: NavigationItemWithSubItemProps) => {
  const [isOpen, setOpen] = useState(false)
  const containerRef = useRef<HTMLUListElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const id = useId()

  const style = useAnchoredPosition({
    active: isOpen,
    container: containerRef,
    anchor: triggerRef,
    horizontalAlignment,
    ...options,
  })

  const onBlur = useCallback((event: React.FocusEvent) => {
    // If focus moves outside the menu + trigger, close
    const nextFocus = event.relatedTarget as Node | null
    if (
      !containerRef.current?.contains(nextFocus) &&
      !triggerRef.current?.contains(nextFocus)
    ) {
      setOpen(false)
    }
  }, [])

  const { zIndex } = useOverlayRegistry()
  // TODO at arrow key navigation

  return (
    <>
      <button
        id={'navigation-' + id}
        ref={triggerRef}
        type="button"

        onClick={() => {
          setOpen(!isOpen)
        }}
        onBlur={onBlur}

        className="navigation-item-button"

        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={'navigation-items-' + id}
      >
        {label}
        <ExpansionIcon isExpanded={isOpen} />
      </button>
      <ul
        id={'navigation-items-' + id}
        ref={containerRef}

        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false)
            event.preventDefault()
            event.stopPropagation()
          }
        }}
        onBlur={onBlur}

        hidden={!isOpen}
        className={clsx(
          'fixed flex-col-4 p-4 bg-surface text-on-surface shadow-side shadow-hw-bottom rounded-md',
          { 'opacity-0': !style }
        )}
        style={{ ...style, zIndex }}
      >
        {!!items && items.length > 0 && items.map(({ link, label, external }, index) => (
          <li key={index}>
            <LinkComponent
              href={link}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              className="navigation-item-link-in-box"
            >
              {label}
            </LinkComponent>
          </li>
        ))}
      </ul>
    </>
  )
}

///
/// NavigationItemList
///
export type NavigationItemListProps = Omit<HTMLAttributes<HTMLElement>, 'children'> & {
  items: NavigationItemType[],
  LinkComponent?: ElementType<NavigationLinkComponentProps>,
}

export const NavigationItemList = ({ items, LinkComponent = DefaultLink, ...restProps }: NavigationItemListProps) => {
  return (
    <ul {...restProps} className={clsx('flex-row-6 items-center', restProps.className)}>
      {items.map((item, index) => (
        <li key={index}>
          {isSubItem(item) ? (
            <NavigationItemWithSubItem {...item} LinkComponent={LinkComponent} />
          ) : (
            <LinkComponent
              href={item.link}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              className="navigation-item-link"
            >
              {item.label}
            </LinkComponent>
          )}
        </li>
      ))}
    </ul>
  )
}

///
/// Navigation
///
export type NavigationProps = NavigationItemListProps

export const Navigation = ({ ...props }: NavigationProps) => {
  const translation = useHightideTranslation()
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const id = useId()
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    menuRef.current?.focus()
  }, [isMobileOpen])

  const { zIndex } = useOverlayRegistry({ isActive: isMobileOpen })

  return (
    <nav>
      <NavigationItemList
        {...props}
        className={clsx('hidden', { 'desktop:flex': !isMobileOpen }, props.className)}
      />
      <IconButton
        tooltip={translation('menu')}
        coloringStyle="text"
        color="neutral"
        onClick={() => setIsMobileOpen(true)}

        className="desktop:hidden"

        aria-haspopup="true"
        aria-expanded={isMobileOpen}
        aria-controls={'navigation-menu-' + id}
      >
        <MenuIcon className="w-6 h-6"/>
      </IconButton>
      <div
        id={'navigation-menu-' + id}
        ref={menuRef}
        hidden={!isMobileOpen}

        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsMobileOpen(false)
            event.preventDefault()
            event.stopPropagation()
          }
        }}

        className={clsx(
          'flex-col-8 items-center justify-center fixed inset-0 p-8 w-screen h-screen bg-surface text-on-surface', {
            'desktop:hidden': isMobileOpen,
          },
          props.className
        )}
        style={{ zIndex }}
      >
        <IconButton
          tooltip={translation('close')}
          coloringStyle="text"
          color="neutral"
          onClick={() => setIsMobileOpen(false)}
        >
          <XIcon/>
        </IconButton>
        <NavigationItemList {...props} className={clsx('flex-col-8', props.className)}/>
      </div>
    </nav>
  )
}